import React from 'react';

export interface SpinnerProps {
  className?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  color?: string;
}

const sizeMap = {
  xs: 'w-3.5 h-3.5',
  sm: 'w-4 h-4',
  md: 'w-6 h-6',
  lg: 'w-8 h-8',
  xl: 'w-10 h-10',
};

/**
 * Universal SVG Spinner animation matching the project's design standard
 */
export const Spinner: React.FC<SpinnerProps> = ({
  className = '',
  size = 'md',
  color = 'text-[#B71C1C]',
}) => {
  const sizeClass = sizeMap[size] || 'w-6 h-6';
  return (
    <svg
      className={`${sizeClass} animate-spin ${color} ${className}`.trim()}
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8v8H4z"
      />
    </svg>
  );
};

export interface LoaderProps {
  text?: string;
  className?: string;
  spinnerSize?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  spinnerColor?: string;
  textColor?: string;
}

/**
 * Universal loader block with spinner & descriptive text
 */
export const Loader: React.FC<LoaderProps> = ({
  text = 'Loading...',
  className = '',
  spinnerSize = 'md',
  spinnerColor = 'text-[#B71C1C]',
  textColor = 'text-gray-400',
}) => {
  return (
    <div className={`flex flex-col items-center justify-center gap-2 ${textColor} ${className}`.trim()}>
      <Spinner size={spinnerSize} color={spinnerColor} />
      {text && <span className="text-xs font-semibold">{text}</span>}
    </div>
  );
};

export interface TableLoaderProps {
  colSpan?: number;
  text?: string;
  className?: string;
  spinnerSize?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
}

/**
 * Standard table loading row
 */
export const TableLoader: React.FC<TableLoaderProps> = ({
  colSpan = 10,
  text = 'Loading...',
  className = 'py-12',
  spinnerSize = 'md',
}) => {
  return (
    <tr>
      <td colSpan={colSpan} className={`text-center text-gray-400 ${className}`.trim()}>
        <div className="flex flex-col items-center justify-center gap-2">
          <Spinner size={spinnerSize} color="text-[#B71C1C]" />
          {text && <span className="text-xs font-semibold">{text}</span>}
        </div>
      </td>
    </tr>
  );
};

export default Loader;
